import i18next from 'i18next';
import { ReactComponent as PendingIcon } from 'assets/icons/pending.svg';
import { ReactComponent as ShippedIcon } from 'assets/icons/shipped.svg';
import { ReactComponent as ProcessingIcon } from 'assets/icons/processing.svg';
import { ReactComponent as HoldIcon } from 'assets/icons/hold.svg';
import VisibilityOffIcon from '@material-ui/icons/VisibilityOff';
import permissions from '../../config/permissions';
import {
  CUSTOM_TAGS_OPTIONS,
  PATIENT_CUSTOM_TAGS_OPTIONS,
  SURVEY_TAGS_OPTIONS,
} from '../Shared/CustomTags';
import { INFO_PANE_PAGES } from '../../redux_store/reducer/reducers/infoPaneReducer';
import FILTER_TYPES from '../Shared/Table/widgets/Filters/constants';

export const ASSESSMENT_STATUSES = {
  invited: 'invited',
  inProgress: 'inProgress',
  pendingReview: 'pendingReview',
  completed: 'completed',
  processing: 'processing',
  scheduled: 'scheduled',
  eTransfer: 'eTransfer',
  canceled: 'canceled',
  noShow: 'noShow',
};

export const ASSESSMENT_STATUSES_OPTIONS = [
  {
    label: i18next.t('tables:invited'),
    value: ASSESSMENT_STATUSES.invited,
    icon: HoldIcon,
    enabledForNonSponsors: false,
  },
  {
    label: i18next.t('tables:inProgress'),
    value: ASSESSMENT_STATUSES.inProgress,
    icon: PendingIcon,
    enabledForNonSponsors: false,
  },
  {
    label: i18next.t('tables:scheduled'),
    value: ASSESSMENT_STATUSES.scheduled,
    icon: ProcessingIcon,
    enabledForNonSponsors: false,
  },
  {
    label: i18next.t('tables:pendingReview'),
    value: ASSESSMENT_STATUSES.pendingReview,
    icon: ProcessingIcon,
    enabledForNonSponsors: true,
    addDivider: true,
  },
  {
    label: i18next.t('tables:completed'),
    value: ASSESSMENT_STATUSES.completed,
    icon: ShippedIcon,
    enabledForNonSponsors: true,
  },
  {
    label: i18next.t('tables:processing'),
    value: ASSESSMENT_STATUSES.processing,
    icon: ShippedIcon,
    enabledForNonSponsors: false,
  },
  {
    label: i18next.t('tables:eTransfer'),
    value: ASSESSMENT_STATUSES.eTransfer,
    icon: ShippedIcon,
    enabledForNonSponsors: false,
  },
  {
    label: i18next.t('tables:canceled'),
    value: ASSESSMENT_STATUSES.canceled,
    icon: ShippedIcon,
    enabledForNonSponsors: false,
  },
  {
    label: i18next.t('tables:noShow'),
    value: ASSESSMENT_STATUSES.noShow,
    icon: VisibilityOffIcon,
    enabledForNonSponsors: false,
  },
];

export const ASSESSMENT_FILTER_LIST = [
  {
    title: 'All',
    id: 'all',
  },
  {
    title: 'Respiratory & Allergy',
    id: 'respiratoryAndAllergy',
  },
  {
    title: 'Emotional & Psychological',
    id: 'emotionalAndPsychological',
  },
  {
    title: 'Lifestyle & Preventive Care',
    id: 'lifestyleAndPreventiveCare',
  },
  {
    title: 'Chronic Pain',
    id: 'chronicPain',
  },
];

export const GET_ASSESSMENTS_COLUMNS = (isPatientPage) => [
  {
    size: 200,
    header: i18next.t('tables:customTags'),
    accessorKey: 'accountCustomTags',
    filter: {
      filterType: FILTER_TYPES.SELECT,
      options: CUSTOM_TAGS_OPTIONS,
    },
    cell: {
      withBubbles: true,
    },
    permission: permissions.ACCOUNTS_GET,
  },
  {
    size: 200,
    header: i18next.t('tables:surveyTags'),
    accessorKey: 'surveyTags',
    filter: {
      filterType: FILTER_TYPES.SELECT,
      options: SURVEY_TAGS_OPTIONS,
    },
    cell: {
      withBubbles: true,
    },
    permission: permissions.ACCOUNTS_GET,
  },
  {
    size: 220,
    header: i18next.t('tables:account'),
    accessorKey: 'accountBusinessName',
    filter: {
      filterType: FILTER_TYPES.SELECT_WITH_DYNAMIC_HINT,
    },
    permission: permissions.ACCOUNTS_GET,
    enableHiding: false,
    isDefaultCell: true,
  },
  {
    size: 260,
    header: i18next.t('tables:patientCustomTags'),
    accessorKey: 'patientCustomTags',
    filter: {
      filterType: FILTER_TYPES.SELECT,
      options: PATIENT_CUSTOM_TAGS_OPTIONS,
    },
    cell: {
      withBubbles: true,
    },
    permission: permissions.ACCOUNTS_GET,
  },
  {
    size: 180,
    header: i18next.t('tables:customId'),
    accessorKey: 'accountCustomIdentifier',
    filter: {},
    permission: permissions.ACCOUNTS_GET,
    enableHiding: false,
    isDefaultCell: true,
  },
  {
    size: 200,
    header: i18next.t('tables:mrnNumber'),
    accessorKey: 'patientMrnNumber',
    filter: {},
    cell: {
      infoPanePage: INFO_PANE_PAGES.patient_page,
    },
  },
  {
    size: 240,
    header: i18next.t('tables:patientFirstName'),
    accessorKey: 'patientFirstName',
    filter: {},
    hidden: isPatientPage,
    cell: {
      infoPanePage: INFO_PANE_PAGES.patient_page,
    },
  },
  {
    size: 240,
    header: i18next.t('tables:patientLastName'),
    accessorKey: 'patientLastName',
    filter: {},
    hidden: isPatientPage,
    cell: {
      infoPanePage: INFO_PANE_PAGES.patient_page,
    },
  },
  {
    size: 200,
    header: i18next.t('tables:patientDOB'),
    accessorKey: 'patientDateOfBirth',
    hidden: isPatientPage,
    filter: { filterType: FILTER_TYPES.DATE },
    isDefaultCell: true,
  },
  {
    size: 230,
    header: i18next.t('tables:assessmentName'),
    accessorKey: 'surveyName',
    filter: {
      filterType: FILTER_TYPES.SELECT_WITH_DYNAMIC_HINT,
    },
    isDefaultCell: true,
  },
  {
    size: 200,
    header: i18next.t('tables:orderDate'),
    accessorKey: 'orderDate',
    filter: {
      shouldRange: true,
      filterType: FILTER_TYPES.DATE,
    },
    isDefaultCell: true,
  },
  {
    size: 250,
    header: i18next.t('tables:appointmentTimestamp'),
    accessorKey: 'appointmentTimestamp',
    filter: {
      shouldRange: true,
      hasNoMaxDate: true,
      filterType: FILTER_TYPES.DATE,
    },
    isDefaultCell: true,
  },
  {
    size: 200,
    header: i18next.t('tables:invitedTimestamp'),
    accessorKey: 'invitedTimestamp',
    filter: {
      shouldRange: true,
      hasNoMaxDate: true,
      filterType: FILTER_TYPES.DATE,
    },
    isDefaultCell: true,
  },
  {
    size: 200,
    header: i18next.t('tables:createdBy'),
    accessorKey: 'createdBy',
    filter: {
      filterType: FILTER_TYPES.SELECT_WITH_DYNAMIC_HINT,
    },
    isDefaultCell: true,
  },
  {
    size: 200,
    header: i18next.t('tables:status'),
    accessorKey: 'status',
    filter: {
      filterType: FILTER_TYPES.SELECT,
      options: ASSESSMENT_STATUSES_OPTIONS,
    },
    cell: {
      withBubble: true,
    },
  },
];

export const INVITATION_TYPE = {
  send: 'send',
  resend: 'resend',
  notAllowed: 'notAllowed',
};
